import { AppThunkAction } from '../reducers';
import { NewPractitionerSpecialtyAssociation } from '../../models/Entities/PractitionerAssociations/Specialties/NewPractitionerSpecialtyAssociation';
import { PractitionerSpecialtyAssociation } from '../../models/Entities/PractitionerAssociations/Specialties/PractitionerSpecialtyAssociation';
import { SpecialtyViewModel } from '../../models/Entities/Specialties/SpecialtyModels';

// actions
import * as Actions from '../actions';
import {
  SPECIALTIES_GETALL_SUCCESS,
  SpecialtiesGetAllSuccessAction
} from '../actions/Specialties.Actions';

// services
import * as Services from '../../services/PractitionerSpecialties.Service';
import * as PractitionerServices from '../../services/Practitioners.Service';
import * as SpecialtyServices from '../../services/Specialties.Services';

type KnownAction =
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_RequestAll_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_ReceiveAll_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Associations_Request_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Associations_Receive_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Associations_Fail_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_FailAll_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Add_Request_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Add_Receive_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Add_Fail_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Delete_Request_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Delete_Receive_Action
  | Actions.PractitionerSpecialtyAssociations.PractitionerSpecialty_Delete_Fail_Action
  | Actions.Practitioner.Practitioners_GetAll_Receive_Action
  | SpecialtiesGetAllSuccessAction;

const enrichWithSpecialty = (
  associations: PractitionerSpecialtyAssociation[],
  specialties: SpecialtyViewModel[]
): PractitionerSpecialtyAssociation[] => {
  return associations.map((assoc) => ({
    ...assoc,
    specialty: specialties.find((s) => s.id === assoc.specialtyId)
  }));
};

export const actionCreators = {
  /**
   * Every practitioner / specialty association of the tenant, in one request to PractitionerAssociation.API.
   * Independent of the practitioners and of the specialty catalogue: the practitioner reducer crosses the three.
   */
  GetAllPractitionerSpecialtyAssociations: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
    const state = getState();
    if (state.oidc?.user?.access_token === undefined) {
      dispatch({
        type: 'PRACTITIONER_SPECIALTY_ASSOCIATIONS_FAIL_ACTION',
        error: { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] }
      });
      return;
    }
    const token: string = state.oidc.user.access_token;
    dispatch({ type: 'PRACTITIONER_SPECIALTY_ASSOCIATIONS_REQUEST_ACTION' });
    return Services.GetAllPractitionerSpecialtyAssociations(token)
      .then((associations) => {
        dispatch({ type: 'PRACTITIONER_SPECIALTY_ASSOCIATIONS_RECEIVE_ACTION', associations });
      })
      .catch((error) => {
        dispatch({ type: 'PRACTITIONER_SPECIALTY_ASSOCIATIONS_FAIL_ACTION', error });
      });
  },

  GetSpecialtiesByPractitionerId:
    (practitionerId: string): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      const state = getState();
      let token: string;

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({
          type: 'PRACTITIONER_SPECIALTY_FAIL_ALL_ACTION',
          error: { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] }
        });
        return;
      } else {
        token = state.oidc.user.access_token;
      }

      if (!getState().Practitioners?.successLoadingAll) {
        PractitionerServices.GetPractitioners(1, 1000, undefined, token).then(
          (practitioners) => {
            dispatch({
              type: 'PRACTITIONERS_GETALL_RECEIVE_ACTION',
              practitioners: practitioners
            });
          }
        );
      }

      dispatch({
        type: 'PRACTITIONER_SPECIALTY_REQUEST_ALL_ACTION',
        practitionerId: practitionerId
      });

      const loadSpecialties = (): Promise<SpecialtyViewModel[]> => {
        const currentState = getState();
        if (
          currentState.Specialties?.successLoadingAll &&
          currentState.Specialties?.list?.length > 0
        ) {
          return Promise.resolve(currentState.Specialties.list);
        }
        return SpecialtyServices.GetAllSpecialties(token).then((specialties) => {
          dispatch({
            type: SPECIALTIES_GETALL_SUCCESS,
            payload: specialties
          });
          return specialties;
        });
      };

      Promise.all([
        Services.GetAllPractitionerSpecialties(practitionerId, token),
        loadSpecialties()
      ])
        .then(([associations, specialties]) => {
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_RECEIVE_ALL_ACTION',
            practitionerId: practitionerId,
            practitionerSpecialties: enrichWithSpecialty(associations, specialties)
          });
        })
        .catch((error) => {
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_FAIL_ALL_ACTION',
            error
          });
        });
    },

  AssociatePractitionerSpecialty:
    (newAssociation: NewPractitionerSpecialtyAssociation): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      const state = getState();
      let token: string;

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({
          type: 'PRACTITIONER_SPECIALTY_ADD_FAIL_ACTION',
          error: { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] }
        });
        return;
      } else {
        token = state.oidc.user.access_token;
      }

      dispatch({
        type: 'PRACTITIONER_SPECIALTY_ADD_REQUEST_ACTION',
        practitionerSpecialty: newAssociation
      });

      Services.AssociatePractitionerSpecialty(newAssociation, token)
        .then((association: PractitionerSpecialtyAssociation) => {
          const specialties = getState().Specialties?.list ?? [];
          const enriched = enrichWithSpecialty([association], specialties)[0];
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_ADD_RECEIVE_ACTION',
            practitionerSpecialty: enriched
          });
        })
        .catch((error) => {
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_ADD_FAIL_ACTION',
            error
          });
        });
    },

  DisassociatePractitionerSpecialtyById:
    (practitionerId: string, associationId: number): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      const state = getState();
      let token: string;

      if (state.oidc?.user?.access_token === undefined) {
        dispatch({
          type: 'PRACTITIONER_SPECIALTY_DELETE_FAIL_ACTION',
          error: { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] }
        });
        return;
      } else {
        token = state.oidc.user.access_token;
      }

      dispatch({
        type: 'PRACTITIONER_SPECIALTY_DELETE_REQUEST_ACTION',
        practitionerSpecialtyId: associationId
      });

      Services.DisassociatePractitionerSpecialtyById(associationId, token)
        .then(() => {
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_DELETE_RECEIVE_ACTION',
            practitionerId: practitionerId,
            practitionerSpecialtyId: associationId
          });
        })
        .catch((error) => {
          dispatch({
            type: 'PRACTITIONER_SPECIALTY_DELETE_FAIL_ACTION',
            error
          });
        });
    }
};
